import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';

import { ApiManager } from '../../managers';
import { CookieValues, Model, ModelNameEnum } from '../../types';
import { AlertContext } from '../Alert';
import { FormInputType } from '../Form/Form.types';
import FormLoader from '../Form/FormLoader';
import { schemaFieldToFormInput } from '../Form/utility';
import EnhancedTable from '../Table/Table';
import { EnhancedTableRef } from '../Table/Table.types';

function DataManager({ model }: { model: ModelNameEnum }) {
  const tableRef = useRef<EnhancedTableRef>(null);

  const { showAlert } = useContext(AlertContext);

  const [cookies] = useCookies<'token', CookieValues>(['token']);

  const [tableData, setTableData] = useState<any>();
  const [modelDoc, setModelDoc] = useState<Model>();
  const [schemaFields, setSchemaFields] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [updateFormInputs, setUpdateFormInputs] = useState<FormInputType[]>([]);
  const [createFormInputs, setCreateFormInputs] = useState<FormInputType[]>([]);

  const loadData = useCallback(async () => {
    setIsLoading(true);

    try {
      // Get data
      const response = await ApiManager.SendRequest<{
        model: Model;
      }>({
        route: 'data',
        action: 'query',
        params: {
          ...(model === ModelNameEnum.configuration && { display: true }),
        },
        model,
      });
      if (response.error) {
        showAlert({
          headerText: 'Uh-oh',
          detailText: response.error,
          variant: 'danger',
          dismissText: 'okay',
        });
        return;
      }

      setSchemaFields(response.model.schemaFields);
      setTableData(response.results);
      setModelDoc(response.model);
    } catch (err: any) {
      showAlert({
        headerText: 'Uh-oh',
        detailText: `An error has occurred, please try again or contact support.\nError: ${err.emssage}`,
        variant: 'danger',
        dismissText: 'okay',
        err,
      });
    } finally {
      setIsLoading(false);
    }
  }, [showAlert, model]);

  const tableDidStartLoading = useCallback(() => {
    setIsLoading(true);
  }, []);

  /**
			@param 	{JSON}		action 		action.type = 'delete, update' and action.data = newly returned data
			@param 	{String}	message 	Message returned from API
			@param 	{String}	error 		Error message or null if not contained

			action:
			{
				type: 'delete',
				data: {record}
			}

	*/
  const tableDidFinishLoading = useCallback((modifiedData: any, message: string | null) => {
    try {
      setTableData(modifiedData);
      showAlert({
        headerText: 'Success',
        detailText: message ?? '',
        variant: 'success',
        dismissText: 'okay',
      });
    } catch (err: any) {
      showAlert({
        headerText: 'Uh-oh',
        detailText: `An error has occurred, please try again or contact support.\nError: ${err.emssage}`,
        variant: 'danger',
        dismissText: 'okay',
        err,
      });
    } finally {
      setIsLoading(false);
    }
  }, [showAlert]);
  /**
   * Fetch data from database and put it into the table
   */
  useEffect(() => {
    if (model) {
      loadData();
    }
  }, [model, loadData]);

  /**
   * First useEffect above will fetch the data from the backend so that the
   * table component can render and give us a ref
   * Then we'll use that ref here to generate the submit buttons for our form inputs
   */
  useEffect(() => {
    if (schemaFields.length && tableRef.current) {
      const regenerateFormFieldsForNewModel = async () => {
        const newUpdateFormInputs = [];
        const newCreateFormInputs = [];
        let formInput: any = null;
        for (let i = 0; i < schemaFields.length; i++) {
          formInput = await schemaFieldToFormInput(schemaFields[i], cookies.token);
          if (formInput !== null) {
            if (schemaFields[i].managedUpdateForm) {
              // TODO: Added this to fix a bug that came up last minute
              // may not be the best fix
              const updateInput = { ...formInput };
              updateInput.managedUpdateForm = true;
              newUpdateFormInputs.push(updateInput);
            }
            newCreateFormInputs.push(formInput);
          }
        }

        const createSubmitButton = tableRef.current?.getCreateSubmitButton();
        if (createSubmitButton) {
          newCreateFormInputs.push(createSubmitButton);
        }

        setUpdateFormInputs([...newUpdateFormInputs]);
        setCreateFormInputs([...newCreateFormInputs]);
      };
      regenerateFormFieldsForNewModel();
    }
  }, [schemaFields, cookies.token]);

  return (
    <>
      <FormLoader isLoading={isLoading} />
      {modelDoc && (
        <EnhancedTable
          ref={tableRef}
          data={tableData}
          headers={modelDoc.tableProperties.headers}
          model={modelDoc.name}
          selectAllEnabled
          multiSelectEnabled
          defaultSort={modelDoc.tableProperties.defaultSort}
          sortEnabled
          tableDidStartLoading={tableDidStartLoading}
          tableDidFinishLoading={tableDidFinishLoading}
          isDeleteAvailable
          isUpdateAvailable
          updateFormInputs={updateFormInputs}
          onUpdateFormInputsChanged={setUpdateFormInputs}
          isCreateAvailable
          createFormInputs={createFormInputs}
          isCsvAvailable
          onCreateFormInputsChanged={setCreateFormInputs}
        />
      )}
    </>
  );
}

export default DataManager;
